import { useRef } from 'react';
import type { Direction, MapObject } from '@etown/shared';
import { ANCHOR_HINT } from '../../game/overlayAnchors';
import { useOverlayAnchor } from '../../hooks/useOverlayAnchor';
import { useInteractionStore } from '../../stores/interactionStore';
import { useViewStore } from '../../stores/viewStore';
import { benutzenTaste } from '../office/benutzenTaste';
import './objects.css';

const OBJECT_LABELS: Record<string, string> = {
  whiteboard: 'Whiteboard öffnen',
  stickyboard: 'Notizen öffnen',
  youtube: 'Video ansehen',
  link: 'Link öffnen',
  screen: 'Bildschirm-Bereich',
  picture: 'Bild ansehen',
  calendar: 'Kalender öffnen',
  fridge: 'Kühlschrank öffnen',
  coffee: 'Kaffeemaschine',
  arcade: 'Spielautomat',
  boardgame: 'Spieltisch',
};

/** Schritt in Blickrichtung des Objekts (Tile-Koordinaten). */
const FACING_STEP: Record<Direction, readonly [number, number]> = {
  down: [0, 1],
  up: [0, -1],
  left: [-1, 0],
  right: [1, 0],
};

/**
 * Wie weit der Anker vor der Vorderkante des Objekts liegt, in Kacheln.
 *
 * Knapp davor, aus zwei Gruenden. Er darf nicht *im* Objekt sitzen: die
 * Projektion blendet Anker hinter Waenden ab, und ein Wandobjekt liegt oft
 * auf der Wandkachel selbst (die Pinnwand im Flur) oder blockiert seine
 * eigene Flaeche (Spielautomat, Spieltisch) — dort galt der Hinweis immer
 * als verdeckt und war nur zu erahnen. Er darf aber auch nicht weit davor
 * liegen: wer naeher herantritt als der Anker, hat ihn hinter sich, und die
 * Projektion blendet ihn aus. Genau das passierte bei einer halben Kachel
 * Abstand, sobald man direkt vor dem Whiteboard stand.
 */
const ANKER_VORSPRUNG_TILES = 0.15;

/** Anker knapp vor der Vorderkante des Objekts. */
export function hinweisAnker(obj: MapObject): { kind: 'world'; x: number; y: number } {
  const [stepX, stepY] = FACING_STEP[obj.facing];
  const halbeTiefe = (stepX !== 0 ? obj.w : obj.h) / 2;
  const vorn = halbeTiefe + ANKER_VORSPRUNG_TILES;
  return {
    kind: 'world',
    x: obj.x + obj.w / 2 + stepX * vorn,
    y: obj.y + obj.h / 2 + stepY * vorn,
  };
}

/** Benutzen-Hinweis ueber dem interagierbaren Objekt. */
export function InteractionHint() {
  const nearby = useInteractionStore((s) => s.nearbyObject);
  // In der Ego-Ansicht drehen Q und E den Blick; benutzt wird dort mit der
  // Leertaste. Die Draufsicht behaelt ihr gewohntes E.
  const mode = useViewStore((s) => s.mode);
  const ref = useRef<HTMLDivElement>(null);
  // Anker vor die Objektmitte (Welt-Koordinaten)
  useOverlayAnchor(ref, nearby ? hinweisAnker(nearby) : { kind: 'world', x: -100, y: -100 }, ANCHOR_HINT);

  if (!nearby) return null;
  return (
    <div ref={ref} className="interaction-hint">
      <kbd>{benutzenTaste(mode)}</kbd> {OBJECT_LABELS[nearby.type] ?? 'Interagieren'}
    </div>
  );
}
